import { IBasePicker, ITag, TagPicker, ValidationState } from 'office-ui-fabric-react';
import React, { FunctionComponent, useCallback, useEffect } from 'react';
import styled from 'styled-components';

import { filterDuplicatesFromOptionsType, IAutocompleteSearchProps, resolveSuggestionsType } from './AutocompleteSearch.types';

export const defaultGetTextFromItem = (item: ITag): string => item.name;

export const defaultResolveSuggestions = (filterText: string, suggestions: ITag[]): ITag[] => {
  const matches = (tag: ITag): boolean => {
    const tagName = tag.name.toLowerCase();
    const filter = filterText.toLowerCase();

    return tagName.startsWith(filter) || (filter.length >= 4 && tagName.includes(filter));
  };

  if (!filterText) return [];
  return suggestions.filter(matches);
};

const filterDuplicatesFromOptions: filterDuplicatesFromOptionsType = (options, items) => {
  return options.filter(option => {
    const itemsNames = items.map(item => item.name);
    return !itemsNames.includes(option.name);
  });
};

const PickerWrapper = styled.div`
  .ms-BasePicker-text {
    &:hover {
      cursor: text;
    }
  }
`;

const onInputTabIndexChange: MutationCallback = mutations => {
  for (const mutation of mutations) {
    const inputElem = mutation.target as Element;
    if (mutation.type === 'attributes' && inputElem && inputElem.getAttribute('tabindex') === '-1') {
      inputElem.setAttribute('tabindex', '0');
    }
  }
};

export const AutocompleteSearch: FunctionComponent<IAutocompleteSearchProps> = ({
  itemLimit = 8,
  resolveDelay = 500,
  resolveSuggestions = defaultResolveSuggestions,
  getTextFromItem = defaultGetTextFromItem,
  noResultsFoundText = 'No results found',
  inputAriaLabel,
  inputAriaLabelledById,
  allowDuplicateSelections = true,
  placeholder,
  onChange,
  selectedItems,
  defaultSelectedItems = [],
  suggestions = [],
  tagPickerStyles = {},
  calloutProps,
  onRenderItem,
}) => {
  const pickerRef = React.createRef<IBasePicker<ITag>>();

  // Set tabindex="0" on the autocomplete input element
  // Unfortunately Fluent-UI does not provide a native way to do this.
  // The only attributes you can set on the input through Fluent-UI are the ones here:
  // https://github.com/microsoft/fluentui/blob/91115267502dc233cd9d513133d52344cef2e1f8/packages/utilities/src/properties.ts#L210
  useEffect(() => {
    const inputElem = document.querySelector('#AutocompleteMajorSelection input.ms-BasePicker-input');
    let observer: MutationObserver | undefined;
    if (inputElem) {
      // Set tabindex=0 on input, then set a MutationObserver that will run anytime tabindex changes and set it back to 0
      inputElem.setAttribute('tabindex', '0');
      observer = new MutationObserver(onInputTabIndexChange);
      observer.observe(inputElem, { attributes: true });
    }
    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, []);

  const onResolveSuggestions: resolveSuggestionsType = useCallback(
    filter => {
      const ref = pickerRef.current;
      let options = resolveSuggestions(filter, suggestions);

      if (!allowDuplicateSelections && ref && ref.items) {
        options = filterDuplicatesFromOptions(options, ref.items);
      }

      return options;
    },
    [allowDuplicateSelections, pickerRef, resolveSuggestions, suggestions]
  );

  const onValidateSuggestionSelection = useCallback(
    (item: ITag | undefined): ITag | null => {
      const ref = pickerRef.current;

      if (!item) return null;

      if (!allowDuplicateSelections) {
        if (ref && ref.items && ref.items.includes(item)) {
          return null;
        }
      }

      return item;
    },
    [allowDuplicateSelections, pickerRef]
  );

  const onValidateInput = useCallback(
    (input: string): ValidationState => {
      // If the input matches a suggestion name
      if (suggestions.find((tag: ITag) => tag.name.toLowerCase() === input.toLowerCase())) {
        return ValidationState.valid;
      } else {
        return ValidationState.invalid;
      }
    },
    [suggestions]
  );

  const onWrapperClicked = useCallback(() => {
    if (pickerRef.current) {
      pickerRef.current.focusInput();
    }
  }, [pickerRef]);

  const finalStyles = {
    input: {
      order: 1,
    },
    itemsWrapper: {
      width: '100%',
      order: 2,
    },
    ...tagPickerStyles,
  };
  return (
    <PickerWrapper onClick={onWrapperClicked} id="AutocompleteMajorSelection">
      <TagPicker
        onRenderItem={onRenderItem}
        removeButtonAriaLabel="Remove"
        onResolveSuggestions={onResolveSuggestions}
        getTextFromItem={getTextFromItem}
        pickerSuggestionsProps={{
          noResultsFoundText,
        }}
        onItemSelected={onValidateSuggestionSelection}
        onValidateInput={onValidateInput}
        itemLimit={itemLimit}
        resolveDelay={resolveDelay}
        inputProps={{
          'aria-label': inputAriaLabel,
          placeholder: placeholder,
          'aria-labelledby': inputAriaLabelledById,
        }}
        componentRef={pickerRef}
        onChange={onChange}
        defaultSelectedItems={defaultSelectedItems}
        selectedItems={selectedItems}
        styles={finalStyles}
        pickerCalloutProps={calloutProps}
      />
    </PickerWrapper>
  );
};

export default AutocompleteSearch;
